import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import ContentContainer from '../../../src/components/ContentContainer';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import { titleCase, formatPhone, formatDate } from '../../../src/utils/format';
import { formatDateTime } from '../../../src/utils/timezone';

/** Shape of GET /api/admin/users/:id/overview (backend/src/routes/admin.ts). All money values are cents. */
interface UserOverview {
  profile: {
    id: string; accountNumber: string; name: string; email: string; phone: string | null;
    isSuspended: boolean; isTestMode: boolean; emailVerified: boolean; smsMarketingOptIn: boolean;
    signupSource: string | null; signupVoucherCode: string | null;
    createdAt: string; lastActiveAt: string | null;
  };
  totals: { washes: number; spendCents: number; refundedCents: number; ratingCount: number; avgRating: number | null };
  recentTransactions: Array<{ id: string; type: string; tierName: string | null; amount: number; status: string; createdAt: string; ownerBusinessName: string | null; locationName: string | null }>;
  bundles: Array<{ id: string; tierName: string | null; washesTotal: number; washesUsed: number; washesRemaining: number; status: string; expiresAt: string | null; purchasedAt: string; ownerBusinessName: string | null }>;
  subscriptions: Array<{ id: string; status: string; cancelAtPeriodEnd: boolean; currentPeriodEnd: string | null; planName: string | null; tierName: string | null; priceMonthly: number; ownerBusinessName: string | null; createdAt: string }>;
  vouchers: Array<{ id: string; code: string; kind: string; discountKind: string | null; percentOff: number | null; tierName: string | null; status: string; redemptionsUsed: number; redemptionLimit: number | null; myRedemptions: number; expiresAt: string | null; ownerBusinessName: string | null }>;
  washCredits: Array<{ id: string; balanceCents: number; expiresAt: string | null; ownerBusinessName: string | null }>;
  points: Array<{ balance: number; lifetimeEarned: number; ownerBusinessName: string | null }>;
  recentLocations: Array<{ id: string; name: string; city: string | null; state: string | null; ownerBusinessName: string | null; visits: number; lastVisit: string | null }>;
  recentScans: Array<{ scannedAt: string; code: string; kind: string; discountKind: string | null; percentOff: number | null; ownerBusinessName: string | null }>;
  problemReports: Array<{ id: string; category: string; status: string; createdAt: string; locationName: string | null }>;
}

const fmtMoney = (cents: number) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Human offer summary for a voucher/scan row. */
function offerLabel(v: { discountKind: string | null; percentOff: number | null; tierName: string | null }): string {
  if (v.discountKind === 'credit') return 'Gift credit';
  if (v.discountKind === 'free_vend') return 'Vending credit';
  if (v.discountKind === 'free_minutes') return 'Free minutes';
  if (v.discountKind === 'free_wash' || v.percentOff === 100) return v.tierName ? `Free ${v.tierName} wash` : 'Free wash';
  if (v.percentOff != null) return `${v.percentOff}% off${v.tierName ? ` ${v.tierName}` : ''}`;
  return v.tierName || v.discountKind || '';
}

/** Small labeled stat used in the totals row. */
function Stat({ label, value, c }: { label: string; value: string; c: ColorTheme }) {
  return (
    <View style={{ minWidth: 90, marginBottom: spacing.sm }}>
      <Text style={{ fontSize: 10, color: c.textLight, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Text>
      <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: c.text, marginTop: 1 }}>{value}</Text>
    </View>
  );
}

/** Small tinted chip (Suspended / Test mode / Verified / status). */
function Chip({ label, color, c }: { label: string; color: string; c: ColorTheme }) {
  return (
    <View style={{ backgroundColor: `${color}15`, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2 }}>
      <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, color }}>{label}</Text>
    </View>
  );
}

/** AdminUserDetail - full customer overview: profile, totals, transactions, plans, rewards, activity. */
export default function AdminUserDetail() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const [data, setData] = useState<UserOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async (signal?: AbortSignal) => {
    try {
      const d = await api<UserOverview>(`/api/admin/users/${id}/overview`);
      if (signal?.aborted) return;
      setData(d);
    } catch (err: any) {
      if (!(signal?.aborted) && !isOffline) {
        toast.show({ type: 'error', title: 'Failed to load customer', message: err?.message ?? 'Unknown error' });
      }
    } finally { if (!(signal?.aborted)) setLoading(false); }
  }, [id]);

  useEffect(() => { const ac = new AbortController(); fetchOverview(ac.signal); return () => ac.abort(); }, [fetchOverview]);

  if (loading) return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  if (!data) return <View style={styles.center}><Text style={styles.empty}>Customer not found</Text></View>;

  const { profile, totals } = data;

  const txStatusColor = (status: string) => {
    if (status === 'completed' || status === 'succeeded') return '#059669';
    if (status === 'refunded') return colors.danger;
    if (status === 'partially_refunded') return '#d97706';
    return colors.textSecondary;
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={720}>

        <TouchableOpacity style={styles.backBtn} onPress={() => router.back()}>
          <Ionicons name="arrow-back" size={20} color={colors.primary} />
          <Text style={styles.backText}>Back to Customers</Text>
        </TouchableOpacity>

        {/* Profile */}
        <Card style={styles.cardGap}>
          <Text style={styles.name}>{titleCase(profile.name) || '(no name)'}</Text>
          <Text style={styles.meta}>{profile.email} · #{profile.accountNumber}{profile.phone ? ` · ${formatPhone(profile.phone)}` : ''}</Text>
          <View style={styles.chipRow}>
            {profile.isSuspended && <Chip label="Suspended" color="#dc2626" c={colors} />}
            {profile.isTestMode && <Chip label="Test mode" color="#7c3aed" c={colors} />}
            <Chip label={profile.emailVerified ? 'Email verified' : 'Email unverified'} color={profile.emailVerified ? '#059669' : '#d97706'} c={colors} />
            {profile.smsMarketingOptIn && <Chip label="SMS opt-in" color="#059669" c={colors} />}
          </View>
          <Text style={styles.joined}>Joined {formatDate(profile.createdAt)}</Text>
          <Text style={styles.lastActive}>Last active: {profile.lastActiveAt ? formatDateTime(profile.lastActiveAt) : 'Never'}</Text>
          {profile.signupVoucherCode ? (
            <Text style={styles.attribution}>Signed up via QR {profile.signupVoucherCode}</Text>
          ) : profile.signupSource ? (
            <Text style={styles.attribution}>Signed up via {profile.signupSource.replace(/_/g, ' ')}</Text>
          ) : null}
        </Card>

        {/* Totals */}
        <Card style={styles.cardGap}>
          <View style={styles.statRow}>
            <Stat label="Washes" value={String(totals.washes)} c={colors} />
            <Stat label="Lifetime spend" value={fmtMoney(totals.spendCents)} c={colors} />
            {totals.refundedCents > 0 && <Stat label="Refunded" value={fmtMoney(totals.refundedCents)} c={colors} />}
            {totals.avgRating != null && <Stat label="Rating" value={`★ ${totals.avgRating.toFixed(1)} · ${totals.ratingCount}`} c={colors} />}
          </View>
        </Card>

        {/* Recent transactions */}
        <Card style={styles.cardGap}>
          <Text style={styles.sectionTitle}>Recent transactions</Text>
          {data.recentTransactions.length === 0 && <Text style={styles.empty}>No transactions</Text>}
          {data.recentTransactions.map(tx => (
            <View key={tx.id} style={styles.itemRow}>
              <View style={styles.itemBody}>
                <Text style={styles.itemTitle}>{tx.tierName || tx.type}</Text>
                <Text style={styles.itemSub} numberOfLines={1}>
                  {[tx.ownerBusinessName, tx.locationName].filter(Boolean).join(' · ')}
                </Text>
                <Text style={styles.itemMeta}>{formatDateTime(tx.createdAt)}</Text>
              </View>
              <View style={styles.itemRight}>
                <Text style={styles.itemAmount}>{fmtMoney(tx.amount)}</Text>
                <Text style={[styles.itemStatus, { color: txStatusColor(tx.status) }]}>{tx.status.replace(/_/g, ' ')}</Text>
              </View>
            </View>
          ))}
        </Card>

        {/* Bundles */}
        {data.bundles.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Bundles</Text>
            {data.bundles.map(b => (
              <View key={b.id} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>
                    {b.washesRemaining}/{b.washesTotal} washes left{b.tierName ? ` · ${b.tierName}` : ''}{b.ownerBusinessName ? ` · ${b.ownerBusinessName}` : ''}
                  </Text>
                  <Text style={styles.itemMeta}>
                    Purchased {formatDate(b.purchasedAt)}{b.expiresAt ? ` · Expires ${formatDate(b.expiresAt)}` : ''}
                  </Text>
                </View>
                <Chip label={b.status} color={b.status === 'active' ? '#059669' : '#d97706'} c={colors} />
              </View>
            ))}
          </Card>
        )}

        {/* Memberships */}
        {data.subscriptions.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Memberships</Text>
            {data.subscriptions.map(s => (
              <View key={s.id} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>
                    {s.planName || s.tierName || 'Membership'} · {fmtMoney(s.priceMonthly)}/mo{s.ownerBusinessName ? ` · ${s.ownerBusinessName}` : ''}
                  </Text>
                  <Text style={styles.itemMeta}>
                    {s.currentPeriodEnd ? `Renews ${formatDate(s.currentPeriodEnd)}` : `Since ${formatDate(s.createdAt)}`}
                  </Text>
                </View>
                <Chip
                  label={s.cancelAtPeriodEnd ? `${s.status} · canceling` : s.status}
                  color={s.cancelAtPeriodEnd ? '#d97706' : s.status === 'active' ? '#059669' : colors.textSecondary}
                  c={colors}
                />
              </View>
            ))}
          </Card>
        )}

        {/* Vouchers (gifts & promos) */}
        {data.vouchers.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Vouchers (gifts & promos)</Text>
            {data.vouchers.map(v => (
              <View key={v.id} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <View style={styles.inlineRow}>
                    <Text style={styles.itemTitle}>{v.code}</Text>
                    <Chip label={v.kind} color={v.kind === 'gift' ? '#7c3aed' : colors.primary} c={colors} />
                  </View>
                  <Text style={styles.itemSub} numberOfLines={1}>
                    {[offerLabel(v), v.ownerBusinessName].filter(Boolean).join(' · ')}
                  </Text>
                  <Text style={styles.itemMeta}>
                    {v.status}{v.myRedemptions > 0 ? ` · used ${v.myRedemptions}×` : ''}{v.expiresAt ? ` · Expires ${formatDate(v.expiresAt)}` : ''}
                  </Text>
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* Wash credits */}
        {data.washCredits.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Wash credits</Text>
            {data.washCredits.map(w => (
              <View key={w.id} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>{fmtMoney(w.balanceCents)}{w.ownerBusinessName ? ` · ${w.ownerBusinessName}` : ''}</Text>
                  {w.expiresAt && <Text style={styles.itemMeta}>Expires {formatDate(w.expiresAt)}</Text>}
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* Points */}
        {data.points.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Points</Text>
            {data.points.map((p, i) => (
              <View key={`${p.ownerBusinessName ?? 'pts'}-${i}`} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>{p.balance.toLocaleString()} points{p.ownerBusinessName ? ` · ${p.ownerBusinessName}` : ''}</Text>
                  <Text style={styles.itemMeta}>{p.lifetimeEarned.toLocaleString()} lifetime earned</Text>
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* Recent locations */}
        {data.recentLocations.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Recent locations</Text>
            {data.recentLocations.map(loc => (
              <View key={loc.id} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>{loc.name}</Text>
                  <Text style={styles.itemSub} numberOfLines={1}>
                    {[loc.city && loc.state ? `${loc.city}, ${loc.state}` : loc.city || loc.state, loc.ownerBusinessName].filter(Boolean).join(' · ')}
                  </Text>
                  <Text style={styles.itemMeta}>
                    {loc.visits} visit{loc.visits === 1 ? '' : 's'}{loc.lastVisit ? ` · Last ${formatDate(loc.lastVisit)}` : ''}
                  </Text>
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* Recent QR scans */}
        {data.recentScans.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Recent QR scans</Text>
            {data.recentScans.map((s, i) => (
              <View key={`${s.code}-${s.scannedAt}-${i}`} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>{s.code}</Text>
                  <Text style={styles.itemSub} numberOfLines={1}>
                    {[offerLabel({ ...s, tierName: null }), s.ownerBusinessName].filter(Boolean).join(' · ')}
                  </Text>
                  <Text style={styles.itemMeta}>{formatDateTime(s.scannedAt)}</Text>
                </View>
              </View>
            ))}
          </Card>
        )}

        {/* Problem reports */}
        {data.problemReports.length > 0 && (
          <Card style={styles.cardGap}>
            <Text style={styles.sectionTitle}>Problem reports</Text>
            {data.problemReports.map(r => (
              <View key={r.id} style={styles.itemRow}>
                <View style={styles.itemBody}>
                  <Text style={styles.itemTitle}>{r.category}</Text>
                  <Text style={styles.itemMeta}>
                    {[r.locationName, formatDateTime(r.createdAt)].filter(Boolean).join(' · ')}
                  </Text>
                </View>
                <Chip label={r.status} color={r.status === 'resolved' ? '#059669' : '#d97706'} c={colors} />
              </View>
            ))}
          </Card>
        )}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  backBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.md },
  backText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  cardGap: { marginBottom: spacing.md },
  name: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  meta: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: 4 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.sm, alignItems: 'center' },
  joined: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.sm },
  lastActive: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginTop: 4 },
  attribution: { fontSize: fontSize.xs, color: c.primary, marginTop: 4 },
  statRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.lg, rowGap: spacing.sm },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  itemRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  itemBody: { flex: 1, minWidth: 0 },
  inlineRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  itemTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  itemSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  itemMeta: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  itemRight: { alignItems: 'flex-end' },
  itemAmount: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  itemStatus: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, marginTop: 2 },
  empty: { fontSize: fontSize.sm, color: c.textLight, paddingVertical: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
